/** Bidirectional URL state for React admin pages (Reports, Sites, Contacts, Users). */

export type UsersUrlState = { roleId?: number; userId?: number; q?: string };

export function parseUsersUrlState(search: string): UsersUrlState {
  const roleId = parseOptionalInt(new URLSearchParams(search), 'roleId');
  const params = new URLSearchParams(search);
  const userId = parseOptionalInt(params, 'userId');
  return {
    roleId: roleId !== undefined && Number.isSafeInteger(roleId) ? roleId : undefined,
    userId: userId !== undefined && Number.isSafeInteger(userId) ? userId : undefined,
    q: parseOptionalString(params, 'q'),
  };
}

export function buildUsersUrl(state: UsersUrlState): string {
  const params = new URLSearchParams();
  if (state.roleId !== undefined && Number.isSafeInteger(state.roleId) && state.roleId > 0) {
    params.set('roleId', String(state.roleId));
  }
  if (state.userId !== undefined && Number.isSafeInteger(state.userId) && state.userId > 0) params.set('userId', String(state.userId));
  if (state.q) params.set('q', state.q);
  const qs = params.toString();
  return qs ? `/users.html?${qs}` : '/users.html';
}

export type ReportsAuditPanel =
  | 'delivery'
  | 'timeline'
  | 'sop'
  | 'sop-flow'
  | 'attempts'
  | 'zoom'
  | 'zoom-calls'
  | 'immix'
  | 'raw';

export type ReportsUrlState = {
  incidentId?: string;
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
  timeFrom?: string;
  timeTo?: string;
  type?: string;
  media?: string;
  /** only | exclude — logged-only (save-only) report queue filter */
  loggedOnly?: string;
  audit?: ReportsAuditPanel;
  /** Open Send report dialog after incident deep-link (SMTP2GO admin recovery). */
  openSend?: boolean;
  /** Comma-separated emails to preselect in Send report. */
  sendRecipients?: string;
};

export type SitesUrlState = {
  siteId?: number;
  tab?: string;
  setup?: 'readiness' | 'guided' | 'quick';
  /** Contact list to open on the embedded Contacts tab (Sites Admin → Contacts). */
  listId?: number;
  /** Contact group to open on the embedded Contacts tab (Sites Admin → Contacts). */
  groupId?: number;
  /** When building URLs, omit tab param when it matches this scope's default. */
  selectionType?: 'global' | 'dealer' | 'site';
};

export type SitesAdminSelectionType = NonNullable<SitesUrlState['selectionType']>;

export type ContactsUrlState = {
  siteId?: number;
  dealerId?: number;
  tab?: 'contacts' | 'groups' | 'lists';
  groupId?: number;
  listId?: number;
  contactId?: number;
  q?: string;
};

function parseOptionalString(params: URLSearchParams, key: string): string | undefined {
  const v = params.get(key);
  if (v == null || v.trim() === '') return undefined;
  return v.trim();
}

function parseOptionalInt(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (raw == null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const REPORTS_AUDIT_VALUES = new Set<string>([
  'delivery',
  'timeline',
  'sop',
  'sop-flow',
  'attempts',
  'zoom',
  'zoom-calls',
  'immix',
  'raw',
]);

export function parseReportsUrlState(search: string): ReportsUrlState {
  const params = new URLSearchParams(search);
  const auditRaw = parseOptionalString(params, 'audit');
  const audit =
    auditRaw && REPORTS_AUDIT_VALUES.has(auditRaw) ? (auditRaw as ReportsAuditPanel) : undefined;
  return {
    incidentId: parseOptionalString(params, 'incidentId'),
    siteId: parseOptionalString(params, 'siteId') ?? parseOptionalString(params, 'site'),
    dateFrom: parseOptionalString(params, 'from') ?? parseOptionalString(params, 'dateFrom'),
    dateTo: parseOptionalString(params, 'to') ?? parseOptionalString(params, 'dateTo'),
    timeFrom: parseOptionalString(params, 'timeFrom'),
    timeTo: parseOptionalString(params, 'timeTo'),
    type: parseOptionalString(params, 'type'),
    media: parseOptionalString(params, 'media'),
    loggedOnly: parseOptionalString(params, 'loggedOnly'),
    audit,
    openSend: params.get('openSend') === '1' || params.get('openSend') === 'true',
    sendRecipients: parseOptionalString(params, 'sendRecipients'),
  };
}

export function buildReportsUrl(state: ReportsUrlState): string {
  const params = new URLSearchParams();
  if (state.incidentId) params.set('incidentId', state.incidentId);
  if (state.siteId) params.set('siteId', state.siteId);
  if (state.dateFrom) params.set('from', state.dateFrom);
  if (state.dateTo) params.set('to', state.dateTo);
  if (state.timeFrom && state.timeFrom !== '00:00') params.set('timeFrom', state.timeFrom);
  if (state.timeTo && state.timeTo !== '23:59') params.set('timeTo', state.timeTo);
  if (state.type) params.set('type', state.type);
  if (state.media) params.set('media', state.media);
  if (state.loggedOnly) params.set('loggedOnly', state.loggedOnly);
  if (state.audit) params.set('audit', state.audit);
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

const SITES_TABS = new Set([
  'summary',
  'details',
  'whitelabel',
  'integrations',
  'contacts',
  'notices',
  'scenarios',
  'technologies',
  'setup',
  'activity',
]);
const SITES_SITE_ONLY_TABS = new Set(['summary', 'integrations', 'scenarios', 'technologies', 'setup']);

export function sitesAdminDefaultTab(selectionType: SitesAdminSelectionType): string {
  return selectionType === 'site' || selectionType === 'dealer' ? 'summary' : 'details';
}

export function normalizeSitesAdminTab(
  tab: string | undefined,
  selectionType: SitesAdminSelectionType,
  opts?: { explicitFromUrl?: boolean },
): string {
  const defaultTab = sitesAdminDefaultTab(selectionType);
  if (!tab || !SITES_TABS.has(tab)) return defaultTab;
  if (selectionType === 'global' && tab === 'summary') return 'details';
  if (selectionType === 'dealer') {
    if (
      tab === 'contacts'
      || tab === 'summary'
      || tab === 'details'
      || tab === 'whitelabel'
      || tab === 'integrations'
      || tab === 'notices'
      || tab === 'activity'
    ) {
      return tab;
    }
    return defaultTab;
  }
  if (selectionType === 'global' && tab === 'notices') return tab;
  if (selectionType !== 'site' && tab === 'summary') return 'details';
  if (selectionType !== 'site' && SITES_SITE_ONLY_TABS.has(tab)) return 'details';
  if (selectionType === 'site' && tab === 'details' && !opts?.explicitFromUrl) return 'summary';
  return tab;
}

function resolveSitesUrlDefaultTab(state: SitesUrlState): string {
  if (state.selectionType) return sitesAdminDefaultTab(state.selectionType);
  if (state.siteId) return 'summary';
  return 'details';
}

export function parseSitesUrlState(search: string): SitesUrlState {
  const params = new URLSearchParams(search);
  const tab = parseOptionalString(params, 'tab');
  const setupRaw = parseOptionalString(params, 'setup');
  const setup =
    setupRaw === 'readiness' || setupRaw === 'guided' || setupRaw === 'quick' ? setupRaw : undefined;
  return {
    siteId: parseOptionalInt(params, 'siteId') ?? parseOptionalInt(params, 'site'),
    tab: tab && SITES_TABS.has(tab) ? tab : undefined,
    setup,
    listId: parseOptionalInt(params, 'listId'),
    groupId: parseOptionalInt(params, 'groupId'),
  };
}

export function buildSitesUrl(state: SitesUrlState): string {
  const params = new URLSearchParams();
  if (state.siteId) params.set('siteId', String(state.siteId));
  const defaultTab = resolveSitesUrlDefaultTab(state);
  if (state.tab && state.tab !== defaultTab) params.set('tab', state.tab);
  if (state.setup) params.set('setup', state.setup);
  if (state.listId) params.set('listId', String(state.listId));
  if (state.groupId) params.set('groupId', String(state.groupId));
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export function parseContactsUrlState(search: string): ContactsUrlState {
  const params = new URLSearchParams(search);
  const tabRaw = parseOptionalString(params, 'tab');
  const tab =
    tabRaw === 'contacts' || tabRaw === 'groups' || tabRaw === 'lists' ? tabRaw : undefined;
  return {
    siteId: parseOptionalInt(params, 'siteId'),
    dealerId: parseOptionalInt(params, 'dealerId'),
    tab,
    groupId: parseOptionalInt(params, 'groupId'),
    listId: parseOptionalInt(params, 'listId'),
    contactId: parseOptionalInt(params, 'contactId') ?? parseOptionalInt(params, 'contact'),
    q: parseOptionalString(params, 'q'),
  };
}

const REACT_CONTACTS_PATH = '/contacts.html';
const REACT_SITES_PATH = '/sites-admin.html';

/** Deep link to the React contacts page (fixed path; safe from other admin pages). */
export function buildReactContactsUrl(state: ContactsUrlState): string {
  const params = new URLSearchParams();
  if (state.siteId) params.set('siteId', String(state.siteId));
  if (state.dealerId) params.set('dealerId', String(state.dealerId));
  if (state.tab && state.tab !== 'contacts') params.set('tab', state.tab);
  if (state.groupId) params.set('groupId', String(state.groupId));
  if (state.listId) params.set('listId', String(state.listId));
  if (state.contactId) params.set('contactId', String(state.contactId));
  if (state.q) params.set('q', state.q);
  const qs = params.toString();
  return qs ? `${REACT_CONTACTS_PATH}?${qs}` : REACT_CONTACTS_PATH;
}

/** Deep link to the React sites admin page (fixed path). */
export function buildReactSitesUrl(state: SitesUrlState): string {
  const params = new URLSearchParams();
  if (state.siteId) params.set('siteId', String(state.siteId));
  const defaultTab = resolveSitesUrlDefaultTab({ ...state, selectionType: state.selectionType ?? (state.siteId ? 'site' : undefined) });
  if (state.tab && state.tab !== defaultTab) params.set('tab', state.tab);
  if (state.setup) params.set('setup', state.setup);
  if (state.listId) params.set('listId', String(state.listId));
  if (state.groupId) params.set('groupId', String(state.groupId));
  const qs = params.toString();
  return qs ? `${REACT_SITES_PATH}?${qs}` : REACT_SITES_PATH;
}

export function buildContactsUrl(state: ContactsUrlState): string {
  const params = new URLSearchParams();
  if (state.siteId) params.set('siteId', String(state.siteId));
  if (state.dealerId) params.set('dealerId', String(state.dealerId));
  if (state.tab && state.tab !== 'contacts') params.set('tab', state.tab);
  if (state.groupId) params.set('groupId', String(state.groupId));
  if (state.listId) params.set('listId', String(state.listId));
  if (state.contactId) params.set('contactId', String(state.contactId));
  if (state.q) params.set('q', state.q);
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export type SopsWorkspaceTab = 'timeline' | 'connect' | 'runtime' | 'narrative';

export type SopsUrlState = {
  scopeType?: 'GLOBAL' | 'DEALER' | 'SITE';
  scopeId?: number;
  sop?: number;
  step?: number;
  tab?: SopsWorkspaceTab;
};

const SOPS_WORKSPACE_TABS = new Set<string>(['timeline', 'connect', 'runtime', 'narrative']);

function parseSopsWorkspaceTab(params: URLSearchParams): SopsWorkspaceTab | undefined {
  const raw = parseOptionalString(params, 'tab');
  if (!raw || !SOPS_WORKSPACE_TABS.has(raw)) return undefined;
  return raw as SopsWorkspaceTab;
}

export function parseSopsUrlState(search: string): SopsUrlState {
  const params = new URLSearchParams(search);
  const scopeRaw = parseOptionalString(params, 'scopeType');
  const scopeType =
    scopeRaw === 'GLOBAL' || scopeRaw === 'DEALER' || scopeRaw === 'SITE' ? scopeRaw : undefined;
  return {
    scopeType,
    scopeId: parseOptionalInt(params, 'scopeId'),
    sop: parseOptionalInt(params, 'sop'),
    step: parseOptionalInt(params, 'step'),
    tab: parseSopsWorkspaceTab(params),
  };
}

export function buildSopsUrl(state: SopsUrlState): string {
  const params = new URLSearchParams();
  if (state.scopeType) {
    params.set('scopeType', state.scopeType);
    if (state.scopeType === 'GLOBAL') {
      params.set('scopeId', '1');
    } else if (state.scopeId) {
      params.set('scopeId', String(state.scopeId));
    }
  }
  if (state.sop) params.set('sop', String(state.sop));
  if (state.step) params.set('step', String(state.step));
  if (state.tab && state.tab !== 'timeline') params.set('tab', state.tab);
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export type DeliveredReportsUrlState = {
  siteId?: number;
  dealerId?: number;
};

export function parseDeliveredReportsUrlState(search: string): DeliveredReportsUrlState {
  const params = new URLSearchParams(search);
  return {
    siteId: parseOptionalInt(params, 'siteId'),
    dealerId: parseOptionalInt(params, 'dealerId'),
  };
}

export function buildDeliveredReportsUrl(state: DeliveredReportsUrlState): string {
  const params = new URLSearchParams();
  if (state.siteId) params.set('siteId', String(state.siteId));
  if (state.dealerId) params.set('dealerId', String(state.dealerId));
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export type DeliveryRulesListUrlState = {
  ruleType?: string;
  status?: string;
  show?: string;
  dealerId?: string;
  siteId?: string;
  search?: string;
};

export function buildDeliveryRulesListUrl(state: DeliveryRulesListUrlState): string {
  const params = new URLSearchParams();
  if (state.ruleType) params.set('ruleType', state.ruleType);
  if (state.status) params.set('status', state.status);
  if (state.show) params.set('show', state.show);
  if (state.dealerId) params.set('dealerId', state.dealerId);
  if (state.siteId) params.set('siteId', state.siteId);
  if (state.search) params.set('search', state.search);
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export function parseDeliveryRulesListUrlState(search: string): DeliveryRulesListUrlState {
  const params = new URLSearchParams(search);
  return {
    ruleType: parseOptionalString(params, 'ruleType'),
    status: parseOptionalString(params, 'status'),
    show: parseOptionalString(params, 'show'),
    dealerId: parseOptionalString(params, 'dealerId'),
    siteId: parseOptionalString(params, 'siteId'),
    search: parseOptionalString(params, 'search'),
  };
}

export type OrchestratorUrlState = {
  /** Delivery-rule execution id to open on Orchestrator (`/orchestrator.html?executionId=`). */
  executionId?: number;
};

const REACT_ORCHESTRATOR_PATH = '/orchestrator.html';

export function parseOrchestratorUrlState(search: string): OrchestratorUrlState {
  const params = new URLSearchParams(search);
  return {
    executionId: parseOptionalInt(params, 'executionId'),
  };
}

/** Same-path builder for Orchestrator URL sync (keeps other query keys out). */
export function buildOrchestratorUrl(state: OrchestratorUrlState): string {
  const params = new URLSearchParams();
  if (state.executionId) params.set('executionId', String(state.executionId));
  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

/**
 * Canonical investigate deep link for a delivery-rule execution run.
 * Keeps `executionId` in the URL while details are open (same pattern as Reports `?incidentId=`).
 */
export function buildReactOrchestratorUrl(state: OrchestratorUrlState): string {
  const params = new URLSearchParams();
  if (state.executionId) params.set('executionId', String(state.executionId));
  const qs = params.toString();
  return qs ? `${REACT_ORCHESTRATOR_PATH}?${qs}` : REACT_ORCHESTRATOR_PATH;
}

/**
 * Replace the current history entry. On same-path query/hash changes, dispatches
 * `popstate` so `useAdminLocationSearch` (and similar listeners) stay in sync —
 * `history.replaceState` alone does not fire that event.
 */
export function replaceAdminUrl(href: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(href, window.location.origin);
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === next) return;
  const pathnameChanged = url.pathname !== window.location.pathname;
  const hashChanged = url.hash !== window.location.hash;
  window.history.replaceState({}, '', next);
  if (!pathnameChanged) {
    window.dispatchEvent(new PopStateEvent('popstate'));
    if (hashChanged) {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  }
}

/** SPA navigation via patched history.pushState (see App.tsx). */
export function pushAdminUrl(href: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(href, window.location.origin);
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === next) return;
  const pathnameChanged = url.pathname !== window.location.pathname;
  const hashChanged = url.hash !== window.location.hash;
  window.history.pushState({}, '', next);
  if (!pathnameChanged) {
    window.dispatchEvent(new PopStateEvent('popstate'));
    if (hashChanged) {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  }
}

export type GradingReviewUrlState = { incidentId?: string; operatorUserId?: number };

export function parseGradingReviewUrlState(search: string): GradingReviewUrlState {
  const params = new URLSearchParams(search);
  const operatorUserId = parseOptionalInt(params, 'operatorUserId');
  return {
    incidentId: parseOptionalString(params, 'incidentId'),
    operatorUserId: operatorUserId !== undefined && Number.isSafeInteger(operatorUserId) ? operatorUserId : undefined,
  };
}

export function buildGradingReviewUrl(state: GradingReviewUrlState): string {
  const params = new URLSearchParams();
  if (state.incidentId?.trim()) params.set('incidentId', state.incidentId.trim());
  if (state.operatorUserId !== undefined && Number.isSafeInteger(state.operatorUserId) && state.operatorUserId > 0) {
    params.set('operatorUserId', String(state.operatorUserId));
  }
  const qs = params.toString();
  return qs ? `/operator-grading-review.html?${qs}` : '/operator-grading-review.html';
}
